import type { FC, MouseEventHandler, ReactNode } from "react"

import { useTranslation } from "react-i18next"
import styled, { css, type FlattenSimpleInterpolation } from "styled-components"

import Button from "~/components/Button"
import { FormFooterContainer } from "~/components/FormFooter"

import Spinner from "./Spinner"
import { Modal } from "./ui"

export const confirmModalCss = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: flex-end;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9000;
  backdrop-filter: blur(5px);
  background-color: #0002;
  overflow: hidden;

  @media (min-width: 640px) {
    align-items: center;
  }
`

export const StyledConfirmModal = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  min-height: 164px;
  background-color: white;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.25);

  @media (min-width: 640px) {
    min-width: 550px;
    width: 50vw;
    max-width: 50vw;
    border-radius: 10px;
  }

  ${FormFooterContainer} {
    width: 100%;
    bottom: 0;
  }
`
export const StyledConfirmInnerModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 30px;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 16px;
  }

  h4 {
    font-weight: 500;

    @media (max-width: 480px) {
      font-size: 1.1rem;
    }
  }
  span {
    font-weight: 800;
    color: var(--blue-dark-font);
  }
  > div {
    li {
      list-style: inside;
    }
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  > p {
    font-size: 16px;
    font-weight: 500;
    color: var(--blue-dark);
    text-align: center;
  }
`

export const ConfirmModal: FC<{
  Heading: FC
  Descriptions?: FC
  onClose: MouseEventHandler<HTMLSpanElement>
  onSubmit: VoidFunction
  isLoading?: boolean
  loadingMessage?: string
  customCancelText?: string
  customSubmitText?: ReactNode
  onBackgroundClick?: MouseEventHandler<HTMLSpanElement>
  modalCss?: FlattenSimpleInterpolation
  hideCancelButton?: boolean
}> = ({
  Heading,
  Descriptions,
  onClose,
  onBackgroundClick,
  onSubmit,
  isLoading,
  loadingMessage,
  customCancelText,
  customSubmitText,
  modalCss,
  hideCancelButton,
}) => {
  const { t } = useTranslation()

  return (
    <Modal
      css={modalCss || confirmModalCss}
      onClose={(e) => {
        onBackgroundClick ? onBackgroundClick(e) : onClose(e)
      }}
    >
      <StyledConfirmModal>
        {isLoading ? (
          <LoadingContainer>
            <Spinner />
            {loadingMessage && <p>{loadingMessage}</p>}
          </LoadingContainer>
        ) : (
          <>
            <StyledConfirmInnerModal>
              <Heading />
              <div>{Descriptions && <Descriptions />}</div>
            </StyledConfirmInnerModal>
            <FormFooterContainer
              style={
                hideCancelButton ? { justifyContent: `flex-end` } : undefined
              }
            >
              {!hideCancelButton && (
                <Button variant="danger" onClick={onClose}>
                  {customCancelText ?? t(`cancel`)}
                </Button>
              )}
              <Button type="submit" onClick={onSubmit}>
                {customSubmitText ?? t(`continue`)}
              </Button>
            </FormFooterContainer>
          </>
        )}
      </StyledConfirmModal>
    </Modal>
  )
}
